/**
 * Reading and saving drops. RLS shows members only published drops whose
 * time has come; admins see everything, which is what makes the admin
 * preview the real page.
 */
import { supabase } from "../../supabaseClient";
import { logEvent } from "../utils/analytics";
import { MASTER_ITEMS } from "../../mockData";
import { loadUserData, saveUserData } from "../utils/userData";
import { setsWindow } from "./dropModel";
import { excludeHeld } from "../constants/collections";

const DROP_COLS = "id, title, description, publish_at, status, published_at, community_post_id";

export async function listDrops({ limit = 60 } = {}) {
  const { data, error } = await supabase.from("drops").select(DROP_COLS)
    .order("publish_at", { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

export async function loadDrop(id) {
  const [{ data: drop, error }, { data: items, error: e2 }] = await Promise.all([
    supabase.from("drops").select(DROP_COLS).eq("id", id).maybeSingle(),
    supabase.from("drop_items").select("id, section, item_type, item_ref, position, snapshot")
      .eq("drop_id", id).order("position"),
  ]);
  if (error) throw error;
  if (e2) throw e2;
  return drop ? { ...drop, items: items || [] } : null;
}

/* The week's sets of the day for a drop (dropModel.setsWindow): drills rows, oldest first. Empty on any error. */
export async function loadWeekSets(drop) {
  const w = setsWindow(drop);
  if (!w) return [];
  try {
    const { data, error } = await excludeHeld(supabase.from("drills").select("id, title, mux_playback_id, sotd_date, sotd_kind, coaching_points")
      .gte("sotd_date", w.from).lte("sotd_date", w.to).or("status.eq.published,status.is.null").is("deleted_at", null))
      .order("sotd_date", { ascending: true }).limit(14);
    if (error) throw error;
    return data || [];
  } catch { return []; }
}

/* Newest published drop; null if there has never been one. */
export async function loadLatestDrop() {
  const { data, error } = await supabase.from("drops").select(DROP_COLS)
    .eq("status", "published").lte("publish_at", new Date().toISOString())
    .order("publish_at", { ascending: false }).limit(1);
  if (error) throw error;
  return data?.[0] ? loadDrop(data[0].id) : null;
}

export async function saveDrop(drop, items) {
  const { data, error } = await supabase.rpc("admin_save_drop", {
    p_drop: drop,
    p_items: items.map(it => ({ section: it.section, item_type: it.item_type, item_ref: it.item_ref, snapshot: it.snapshot || {} })),
  });
  if (error) throw error;
  return data;
}

export async function deleteDrop(id) {
  const { error } = await supabase.rpc("admin_delete_drop", { p_id: id });
  if (error) throw error;
}

/* One paper read, shaped like the Learning Lab's paper cards. */
export async function loadPaper(resourceId) {
  const [{ data: r }, { data: pr }] = await Promise.all([
    supabase.from("resources").select("id, data").eq("id", resourceId).maybeSingle(),
    supabase.from("paper_reads").select("finding, summary, questions").eq("resource_id", resourceId).maybeSingle(),
  ]);
  if (!r) return null;
  const d = r.data || {};
  return { id: r.id, title: d.title, authors: d.authors, year: d.year, journal: d.journal,
    abstract: d.abstract, url: d.doi_url, finding: pr?.finding, summary: pr?.summary, questions: pr?.questions };
}

/**
 * Open an item the way the rest of the platform opens it: the same session
 * stashes the library, the Learning Lab and the plan builder already read.
 * Papers return "sheet": the drop page shows those itself.
 */
export function openDropItem(item, go, { dropId } = {}) {
  logEvent("drop_item_opened", { drop_id: dropId, item_type: item.item_type, section: item.section, item_ref: item.item_ref });
  const ref = item.item_ref;
  const stash = (k, v) => { try { sessionStorage.setItem(k, typeof v === "string" ? v : JSON.stringify(v)); } catch { /* private window */ } };
  switch (item.item_type) {
    case "drill":
    case "xo":
    case "insight": {
      const section = item.snapshot?.section || (item.item_type === "xo" ? "xo" : item.item_type === "insight" ? "insights" : "pd");
      stash("bam_open_drill", { sectionId: section, id: ref });
      go(section);
      return "nav";
    }
    case "masterclass": {
      const m = MASTER_ITEMS.find(x => x.moduleKey === ref || String(x.id) === ref);
      if (m) stash("bam_open_drill", { sectionId: "master", id: m.id });
      go("master");
      return "nav";
    }
    case "concept":
      stash("bam_open_concept", ref);
      go("learn");
      return "nav";
    case "research":
      go(`research/${ref}`);
      return "nav";
    case "plan":
      stash("bam_open_session", ref);
      go("plans");
      return "nav";
    case "set":
      // A set of the day opens on the Playbook's sets, at that set (SetsFeed reads bam_open_sotd).
      stash("bam_open_sotd", { id: ref });
      go("playbook");
      return "nav";
    case "paper":
      return "sheet";
    default:
      return null;
  }
}

// ── A coach's way through one drop ──────────────────────────────────────
// user_data "drop_progress": { [dropId]: { [sectionId]: iso, posted: iso } }.
// A section counts once anything in it has been opened; the last step counts
// once the coach has replied in the drop's Community thread.
const PROGRESS_KEY = "drop_progress";

export async function loadDropProgress(drop) {
  let mine = {};
  try { mine = { ...((await loadUserData(PROGRESS_KEY, {}))?.[drop.id] || {}) }; } catch { /* none yet */ }
  if (!mine.posted && drop.community_post_id) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { count } = await supabase.from("community_replies").select("id", { count: "exact", head: true })
          .eq("post_id", drop.community_post_id).eq("user_id", user.id);
        if (count > 0) mine.posted = new Date().toISOString();
      }
    } catch { /* offline: just not ticked */ }
  }
  return mine;
}

export async function markDropSection(dropId, section) {
  try {
    const all = (await loadUserData(PROGRESS_KEY, {})) || {};
    const cur = all[dropId] || {};
    if (cur[section]) return;
    await saveUserData(PROGRESS_KEY, { ...all, [dropId]: { ...cur, [section]: new Date().toISOString() } });
  } catch { /* the tick shows for this visit either way */ }
}
